import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
// Type-only import — runtime'da @supabase/supabase-js modülü buraya
// hiç çekilmez (TS derlerken tamamen siler). Supabase artık lazy
// getSupabase() ile yükleniyor (@lib/supabase).
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';

export interface AuthMe {
  userId: string;
  tenantId: string;
  tenantAdi: string;
  tenantSlug: string;
  plan: string;
  email: string;
  ad: string;
  soyad: string;
  izinler: string[];
  kvkkAcikRiza: boolean;
  logoUrl: string | null;
  portalGunlukLimit: number;
  /** Lisans pasif: dolu = firma salt-okunur, bu tarihe (ISO) kadar veri indirilebilir. */
  saltOkunurBitis?: string | null;
}

interface AuthState {
  session: Session | null;
  supabaseUser: SupabaseUser | null;
  me: AuthMe | null;
  loading: boolean;            // initial bootstrap
  setSession: (s: Session | null) => void;
  setMe: (me: AuthMe | null) => void;
  setLoading: (l: boolean) => void;
  clear: () => void;
  // Convenience accessors
  token: () => string | null;
  hasIzin: (izin: string) => boolean;
}

/**
 * Auth store — `me` (profil + izinler + tenant) localStorage'a persist edilir.
 * Sayfa yenilemede /auth/me cevabini beklemeden UI hemen render eder
 * (useMe queryFn arka planda revalidate eder — stale-while-revalidate).
 * Session Supabase tarafindan zaten kendi storage key'inde persist ediliyor,
 * burada SADECE `me` saklanir.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      supabaseUser: null,
      me: null,
      loading: true,
      setSession: (session) =>
        set({ session, supabaseUser: session?.user ?? null }),
      setMe: (me) => set({ me }),
      setLoading: (loading) => set({ loading }),
      clear: () => set({ session: null, supabaseUser: null, me: null }),
      token: () => get().session?.access_token ?? null,
      hasIzin: (izin) => {
        const izinler = get().me?.izinler ?? [];
        if (izinler.includes('*')) return true;
        if (izinler.includes(izin)) return true;
        const [resource] = izin.split(':');
        return izinler.includes(`${resource}:*`);
      },
    }),
    {
      name: 'zoprey-auth-me',
      storage: createJSONStorage(() => localStorage),
      // Sadece `me` persist edilsin — session Supabase'in kendi key'inde
      partialize: (state) => ({ me: state.me }),
    },
  ),
);
